import i18next from 'i18next';
import LocaleKeys from '../locales/keys';
import {
  INTRO_PRICING_KEY,
  INTRO_PRICING_DISCOUNT_KEY,
  TRUE,
  MONTHLY,
} from '../constants/introPricingProperties';
import { getCurrencyDetails } from './common';

const getFirstValueIfAvailable = (additionalProperty) =>
  additionalProperty.Values &&
  additionalProperty.Values.length > 0 &&
  additionalProperty.Values[0];

const getDiscountPrice = (price, discountData) => {
  let discountPrice;
  if (discountData?.DiscountType === 1) {
    discountPrice = price - (price / 100) * discountData?.DiscountAmount;
  } else {
    discountPrice = price - discountData?.DiscountAmount;
  }
  return discountPrice;
};

const getDynamicText = (key, interpolationNode = {}) =>
  i18next.t(key, interpolationNode);

const getDiscountPeriod = (
  discountData,
  discountPeriod,
  localKeySingular,
  localKeyPlural,
) =>
  discountData?.DiscountPeriod === 1
    ? getDynamicText(localKeySingular, {
        discountPeriod,
      })
    : getDynamicText(localKeyPlural, {
        discountPeriod,
      });

const getDiscountPeriodAndBillingCycleText = (
  discountData,
  discountPeriod,
  monthly,
) => {
  if (discountData?.DiscountBillingCycle === monthly) {
    return {
      discountPeriodText: getDiscountPeriod(
        discountData,
        discountPeriod,
        LocaleKeys.common.introPricing.introPriceDiscountPeriodSingularMonth,
        LocaleKeys.common.introPricing.introPriceDiscountPeriodPluralMonths,
      ),
      discountBillingCycleText: getDynamicText(
        LocaleKeys.common.introPricing.singularPerMonth,
      ),
    };
  }
  return {
    discountPeriodText: getDiscountPeriod(
      discountData,
      discountPeriod,
      LocaleKeys.common.introPricing.introPriceDiscountPeriodSingularYear,
      LocaleKeys.common.introPricing.introPriceDiscountPeriodPluralYears,
    ),
    discountBillingCycleText: getDynamicText(
      LocaleKeys.common.introPricing.singularPerYear,
    ),
  };
};

const getTranslateIntoPricing = (
  currencySymbol,
  discountPrice,
  discountPeriodText,
  afterDiscountPrice,
  discountBillingCycleText,
) => {
  const discountPriceLineCurrencySymbol = getDynamicText(
    LocaleKeys.common.introPricing.discountPriceLineCurrencySymbol,
    { currencySymbol },
  );
  const discountPriceText = getDynamicText(
    LocaleKeys.common.introPricing.discountPriceText,
    { discountPrice },
  );
  const discountPricePeriod = getDynamicText(
    LocaleKeys.common.introPricing.discountPricePeriod,
    { discountPeriodText },
  );

  const afterDiscountPrefix = getDynamicText(
    LocaleKeys.common.introPricing.afterDiscountPrefix,
    { discountBillingCycleText },
  );
  const afterDiscountPriceText = getDynamicText(
    LocaleKeys.common.introPricing.afterDiscountPrice,
    {
      afterDiscountPrice,
    },
  );
  const afterDiscountSuffix = getDynamicText(
    LocaleKeys.common.introPricing.afterDiscountSuffix,
    {
      discountBillingCycleText,
    },
  );
  const introPricingLabel = getDynamicText(
    LocaleKeys.common.introPricing.introPricingLogo,
  );
  return {
    discountPriceLineCurrencySymbol,
    discountPriceText,
    discountPricePeriod,
    afterDiscountPrefix,
    afterDiscountPriceText,
    afterDiscountSuffix,
    introPricingLabel,
  };
};

export const parseIntroPricing = (pricingPlan) => {
  const introPricingPlan = (pricingPlan?.AdditionalProperties || []).reduce(
    (introPricing, additionalProperty) => {
      if (additionalProperty.ExternalReference === INTRO_PRICING_KEY) {
        introPricing.isIntroPricing =
          getFirstValueIfAvailable(additionalProperty) === TRUE;
      }

      if (additionalProperty.ExternalReference === INTRO_PRICING_DISCOUNT_KEY) {
        const jsonDiscountDataString =
          getFirstValueIfAvailable(additionalProperty);
        let discountData = {};
        try {
          discountData = JSON.parse(jsonDiscountDataString) || {};
        } catch (e) {
          discountData = {};
        }
        /* DiscountData structure
            DiscountAmount	Percentage or absolute amount to discount.	25 would for percentage be 25% off and for absolute it would be $25 off or whatever the currency is of the pricing plan.
            DiscountBillingCycle	Billing cycle type: 2 = Monthly 5 = Annual	 
            DiscountPeriod	The number of billing cycles the discounts is applied including initial payment	3 on a monthly pricing plan is 3 months at the discount price which would be the initial purchase charge plus 2 renewal charges.
            DiscountType	Discount type: 1 = Percentage 2 = Absolute Amount	 
            DiscountDescription	Description	e.g. "25% off for 3 months"
          */
        const { Currency, RenewalChargeAmount } = pricingPlan;
        const getAmountPrice = getCurrencyDetails(
          RenewalChargeAmount,
          Currency,
        );
        const { currentCurrency, prefixCurrencySymbol, currencySymbol } =
          getAmountPrice;

        const afterDiscountPrice = getAmountPrice.price;

        const discountPrice = getDiscountPrice(
          pricingPlan?.RenewalChargeAmount,
          discountData,
        ).toFixed(currentCurrency.fractionSize);

        const discountPeriod = discountData?.DiscountPeriod;
        const getPeriodAndBillingCycletextNode =
          getDiscountPeriodAndBillingCycleText(
            discountData,
            discountPeriod,
            MONTHLY,
          );

        const discountPeriodText =
          getPeriodAndBillingCycletextNode.discountPeriodText;
        const discountBillingCycleText =
          getPeriodAndBillingCycletextNode.discountBillingCycleText;
        const introPricingNode = getTranslateIntoPricing(
          currencySymbol,
          discountPrice,
          discountPeriodText,
          afterDiscountPrice,
          discountBillingCycleText,
        );
        introPricing = {
          ...introPricing,
          prefixCurrencySymbol,
          introPricingLabel: introPricingNode.introPricingLabel,
          discountPriceLineCurrencySymbol:
            introPricingNode.discountPriceLineCurrencySymbol,
          discountPrice: introPricingNode.discountPriceText,
          discountPricePeriod: introPricingNode.discountPricePeriod,
          afterDiscountPrefix: introPricingNode.afterDiscountPrefix,
          afterDiscountPrice: introPricingNode.afterDiscountPriceText,
          afterDiscountSuffix: introPricingNode.afterDiscountSuffix,
        };
      }
      return introPricing;
    },
    {},
  );
  return introPricingPlan;
};
